// @givmo/org-console — the access editor's MODEL: draft state, the diff that becomes the PUT, and the
// demotion's choice that becomes the role change.
//
// PURE, and deliberately separate from the dialog. The dialog is a form; this is the part that has to
// be RIGHT — an authorization surface where an over-broad patch grants something nobody asked for and
// an under-broad one silently drops a narrowing an admin thought they saved. Keeping it callable
// without a DOM is what lets the wire shape be pinned case by case rather than inferred from clicks.
//
// The backend model it serves:
//
//   * INVITATION-BASED (D-CPO-24). A Global Admin holds every app by tier; anyone else holds exactly
//     the apps they were invited into or later granted, so an app this editor does not show as held
//     is one the person cannot open.
//   * A DEMOTION IS A CHOICE, NOT A DIFF (OM-57, D-CPO-64). A Global Admin made a regular admin keeps
//     exactly the apps the person making the change turns on, with nothing pre-checked and nothing
//     kept implicitly, and it cannot be saved with none. It rides the role change
//     (`buildRoleChange`), never the PUT.
//   * The PUT is PARTIAL and idempotent: an absent facet is left alone, so re-asserting the same
//     state writes nothing. That is why this module emits a DIFF and not a snapshot — sending the
//     whole state back would still be correct, but it would turn every "Save" into an entry in the
//     record people come here to read, and make that record useless for seeing what changed.
//   * `permissions` is THREE-VALUED: absent leaves the custom set, `null` CLEARS it back to tier
//     defaults, a list sets it. The backend recovers that distinction from the fields the body
//     actually names, so "leave it" versus "clear it" is a real wire difference this module has to
//     get right, not a stylistic one.
//   * A `permissions` list for a module the admin neither holds NOR is gaining in the same patch is
//     REFUSED (422), never silently accepted — which falls out of the diff below, because a
//     permissions change is only ever emitted for a module the draft holds, and `has_access: true`
//     rides along whenever that is new.
//   * A custom set may RAISE within the module's catalog, not only narrow: an owner-level string is
//     grantable to a module Member without promoting them. The catalog is the boundary; the tier is
//     only the default.
//   * The External mark (§OM-53) is a LABEL that rides the PUT as `external`, only when it moved.
//     The role change's body takes no such key, so a demotion that also moves the mark is two writes:
//     the role change, then the mark's own PUT ({@link buildExternalMarkPatch}). Neither touches the
//     other's facts, so the record says each change once.
//
// ⚠ THE CATALOG IS A LIST HERE. On this surface the module catalog arrives as a paginated list of
// rows, not as a named wrapper object. A reader that reached for a wrapper key would find nothing,
// coerce to an empty list, and render an editor with no module rows at all.

import { firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';
import { moduleTier, ROLE_MEMBER, ROLE_OWNER, type OrgTier } from './org-tiers';
import type {
  OrgConsoleMemberRow,
  OrgModuleAccessPatch,
  OrgModuleCatalogRow,
  OrgRoleChangeBody,
  UpdateOrgMemberAccessBody,
} from './wire-types';

/** The permission tiers a catalog key can belong to, as the backend spells them. */
export const LEVEL_MEMBER = 'member';
export const LEVEL_OWNER = 'owner';

/** The machine-readable code for a permission key outside the module's catalog. */
export const UNKNOWN_PERMISSION_KEY = 'UNKNOWN_PERMISSION_KEY';
/** The machine-readable code for a patch that would take an admin's stored facts to zero. */
export const LAST_MODULE_REVOKE_REFUSED = 'LAST_MODULE_REVOKE_REFUSED';
/**
 * The machine-readable code for a REMOVAL that would leave the organization with no Global Admin.
 *
 * The console's own translation of each class writer's last-Owner guard — the guard itself lives in
 * the class writer, which is the only place it cannot disagree with the write it gates.
 */
export const LAST_GLOBAL_ADMIN_REFUSED = 'LAST_GLOBAL_ADMIN_REFUSED';

/** The role change's refusals (OM-57): the choice broke one of `org_role_choice.py`'s rules. */
export const ROLE_CHANGE_CHOICE_REQUIRED = 'ROLE_CHANGE_CHOICE_REQUIRED';
export const ROLE_CHANGE_MODULES_WITH_GLOBAL_ADMIN = 'ROLE_CHANGE_MODULES_WITH_GLOBAL_ADMIN';
export const ROLE_CHANGE_MODULE_UNAVAILABLE = 'ROLE_CHANGE_MODULE_UNAVAILABLE';
export const ROLE_CHANGE_DUPLICATE_MODULE = 'ROLE_CHANGE_DUPLICATE_MODULE';
/** The 409: the person is no longer a Global Admin, and holds other apps than the choice. */
export const ROLE_CHANGE_NOT_GLOBAL_ADMIN = 'ROLE_CHANGE_NOT_GLOBAL_ADMIN';

/** The editor's working state for ONE module. */
export interface ModuleDraft {
  /** The backend module slug. */
  module: string;
  /** Does this admin hold the module at all (the grant). */
  hasAccess: boolean;
  /** The module tier the grant carries. */
  tier: OrgTier;
  /**
   * `null` = use the tier defaults (no custom set). An array = a custom set, INCLUDING the empty
   * array, which is a legitimate custom set meaning "holds the module and none of its permissions"
   * — the backend treats presence, not length, as the signal.
   */
  custom: readonly string[] | null;
}

/** The editor's whole working state for one admin. */
export interface AccessDraft {
  isGlobalAdmin: boolean;
  /** One entry per CATALOG module, in catalog order. */
  modules: readonly ModuleDraft[];
  /** The External mark (§OM-53): a label about the person, independent of their tier. */
  external: boolean;
}

/** The per-module row the read reports, looked up by slug. */
function rowFor(member: OrgConsoleMemberRow, module: string) {
  return member.modules.find((row) => row.module === module);
}

/**
 * The admin's CURRENT standing — both the draft the dialog opens with AND the baseline every patch is
 * diffed against.
 *
 * ONE function for both, deliberately: a draft seeded from one reading and diffed against another is
 * how a dialog that looks unchanged saves three modules.
 *
 * ⚠ A GLOBAL ADMIN'S ROWS ARE NOT STORED STATE. Every row of theirs reads "has access, Owner" because
 * the TIER is the authority — the resolver never consults their grant rows — so seeding the editor
 * from that would pre-check every app the moment their tier came off. A Global Admin therefore seeds
 * from {@link demotionBaseline}: nothing held, so the person making the change turns on exactly the
 * apps they keep.
 */
export function initialDraft(
  member: OrgConsoleMemberRow,
  catalog: readonly OrgModuleCatalogRow[],
): AccessDraft {
  if (member.is_global_admin) {
    return { isGlobalAdmin: true, modules: demotionBaseline(catalog), external: member.external };
  }
  return {
    isGlobalAdmin: false,
    external: member.external,
    modules: catalog.map((entry) => {
      const row = rowFor(member, entry.module);
      return {
        module: entry.module,
        hasAccess: row?.has_access === true,
        tier: moduleTier(row?.granted_role),
        // `custom_permissions` is a BOOLEAN flag ("a custom set exists"); the set itself is only
        // observable through `effective_permissions`, which for a custom set IS that set intersected
        // with the catalog.
        custom: row?.custom_permissions === true ? [...row.effective_permissions] : null,
      };
    }),
  };
}

/**
 * The per-module state a DEMOTION starts from — what the editor shows the moment a Global Admin
 * toggle is turned off: EVERY APP OFF (D-CPO-64's Also, Jonathan 2026-10-22: "nothing pre-checked").
 *
 * ⚠ NEVER SEEDED FROM `default_allow`. The role change writes exactly the apps the choice names and
 * nothing else, so a row this seeded as held would be a pre-checked box — and the catalog's
 * `default_allow` says nothing about what this person keeps (a class's legacy apps can still read
 * `true` there, while the server keeps none of them implicitly).
 */
export function demotionBaseline(catalog: readonly OrgModuleCatalogRow[]): ModuleDraft[] {
  return catalog.map((entry) => ({
    module: entry.module,
    hasAccess: false,
    tier: ROLE_MEMBER,
    custom: null,
  }));
}

/** The draft takes a Global Admin's tier away: the act {@link buildRoleChange} carries. */
export function isDemotion(draft: AccessDraft, baseline: AccessDraft): boolean {
  return baseline.isGlobalAdmin && !draft.isGlobalAdmin;
}

/**
 * The role change's body for a DEMOTION — `{role: "Member", modules: [{module, role}]}`, exactly the
 * apps the draft holds, in catalog order — or `null` when the draft is not a demotion or holds no app.
 *
 * `null` for "no app" is the save rule itself: a demotion cannot be saved with nothing chosen
 * (D-CPO-64's Also). Custom permission sets are not part of the choice — the role change grants each
 * app at its level's defaults, and a set is customized afterwards, as for anyone else.
 *
 * `shownModules` (OM-95): the apps the dialog drew, given only when it left out an app the backend
 * serves. The body then names them, so the demotion keeps the person's stored rows for the apps this
 * build never showed (D-CPO-77: "Hiding an app never takes it away from anyone").
 */
export function buildRoleChange(
  draft: AccessDraft,
  baseline: AccessDraft,
  shownModules?: readonly string[],
): OrgRoleChangeBody | null {
  if (!isDemotion(draft, baseline)) return null;
  const modules = draft.modules
    .filter((module) => module.hasAccess)
    .map((module) => ({ module: module.module, role: module.tier }));
  if (modules.length === 0) return null;
  return shownModules === undefined
    ? { role: ROLE_MEMBER, modules }
    : { role: ROLE_MEMBER, modules, shown_modules: [...shownModules] };
}

/**
 * Whether `userId` is the organization's only Global Admin, as far as the loaded roster can say.
 *
 * ⚠ ONLY A COMPLETE ROSTER CAN SAY SO. The roster pages, and another Global Admin may sit on a page
 * not loaded yet; an incomplete one answers `false`, and the server's last-Owner guard stays the
 * backstop. Wrongly answering `true` would block a legal demotion, so the doubt goes the other way.
 */
export function isOnlyGlobalAdmin(
  roster: readonly OrgConsoleMemberRow[],
  userId: string,
  rosterComplete: boolean,
): boolean {
  return rosterComplete && !roster.some((row) => row.user_id !== userId && row.is_global_admin);
}

/** The same choice: the same role, and the same apps at the same levels, in the same order. */
export function sameRoleChange(a: OrgRoleChangeBody | null, b: OrgRoleChangeBody | null): boolean {
  if (a === null || b === null) return a === b;
  const left = a.modules ?? [];
  const right = b.modules ?? [];
  return (
    a.role === b.role &&
    left.length === right.length &&
    left.every((m, i) => m.module === right[i]?.module && m.role === right[i]?.role)
  );
}

/** Catalog lookup by module slug. */
export function catalogFor(
  catalog: readonly OrgModuleCatalogRow[],
  module: string,
): OrgModuleCatalogRow | undefined {
  return catalog.find((entry) => entry.module === module);
}

/** The catalog keys for one tier, in declaration order. */
export function keysAtLevel(entry: OrgModuleCatalogRow | undefined, level: string): string[] {
  return (entry?.permissions ?? []).filter((p) => p.level === level).map((p) => p.key);
}

/**
 * The tier defaults for a module at a tier — what "no custom set" resolves to.
 *
 * Owner ⇒ the Owner catalog UNION the Member catalog (a module Owner holds the module's day-to-day
 * strings too). Member ⇒ the member-level keys only.
 */
export function tierDefaults(entry: OrgModuleCatalogRow | undefined, tier: OrgTier): string[] {
  const member = keysAtLevel(entry, LEVEL_MEMBER);
  return tier === ROLE_OWNER ? [...member, ...keysAtLevel(entry, LEVEL_OWNER)] : member;
}

/** Whether a module has anything customizable at all. An EMPTY catalog means "the grant IS the access". */
export function isCustomizable(entry: OrgModuleCatalogRow | undefined): boolean {
  return (entry?.permissions ?? []).length > 0;
}

/** Order-insensitive set comparison — the wire order of a permissions list carries no meaning. */
function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((key) => seen.has(key));
}

/** One module's draft, keyed for diffing. */
function byModule(modules: readonly ModuleDraft[]): Map<string, ModuleDraft> {
  return new Map(modules.map((module) => [module.module, module]));
}

/**
 * The PUT body for `draft` against `baseline`, or `null` when nothing changed.
 *
 * `null` is the whole point of returning it: an unchanged save is a no-op the backend would accept,
 * write nothing for, and record nothing for — but firing it anyway means every closed dialog looks
 * like a decision in the trail a Global Admin reads to find out who changed what.
 *
 * ⚠ A DRAFT THAT IS A GLOBAL ADMIN EMITS NO `modules`. Their tier subsumes every module (grants are
 * not consulted for them), so a per-module patch would write grant rows that change nothing a person
 * can observe — and the dialog renders those rows read-only for exactly that reason, so there is no
 * divergence to carry.
 *
 * ⚠ A DEMOTION IS NEVER A PATCH: `null` here, and {@link buildRoleChange} carries it. The PUT's tier
 * switch keeps a demoted admin's old apps through OM-57's window (the backend's old role doors),
 * which is exactly the implicit keep D-CPO-64 forbids — so this console never sends it. A demotion's
 * moved mark is its own PUT, {@link buildExternalMarkPatch}.
 *
 * The External mark rides the patch only when it moved, so a Save that changed nothing else writes no
 * Activity row for it, and an unchanged mark never reaches the wire.
 */
export function buildAccessPatch(
  draft: AccessDraft,
  baseline: AccessDraft,
): UpdateOrgMemberAccessBody | null {
  if (isDemotion(draft, baseline)) return null;
  const body: UpdateOrgMemberAccessBody = {};

  if (draft.isGlobalAdmin) {
    // With a demotion ruled out, the only tier move left is the promotion.
    if (!baseline.isGlobalAdmin) body.is_global_admin = true;
  } else {
    const before = byModule(baseline.modules);
    const modules: Record<string, OrgModuleAccessPatch> = {};

    for (const module of draft.modules) {
      const patch = moduleDiff(module, before.get(module.module));
      if (patch !== null) modules[module.module] = patch;
    }

    if (Object.keys(modules).length > 0) body.modules = modules;
  }

  if (draft.external !== baseline.external) body.external = draft.external;

  return Object.keys(body).length > 0 ? body : null;
}

/**
 * The mark's own PUT for a DEMOTION that also moved it — `{external}` — or `null` when it did not move.
 *
 * ⚠ IT GOES OUT AFTER THE ROLE CHANGE LANDS, NEVER BEFORE. Every refusal of the role change says
 * nothing was saved, and that stays true only while the mark waits for it. The role change never
 * touches the mark, so the order changes nothing the record says.
 */
export function buildExternalMarkPatch(
  draft: AccessDraft,
  baseline: AccessDraft,
): UpdateOrgMemberAccessBody | null {
  return draft.external !== baseline.external ? { external: draft.external } : null;
}

/**
 * One module's patch, or `null` when that module is unchanged.
 *
 * REVOKING IS TERMINAL. `has_access: false` removes the grant and its custom set unconditionally, so
 * a tier or a permissions list beside it would describe a grant that will not exist a moment later.
 * Emitting only the revoke keeps the request and the record saying the one thing that happened.
 */
function moduleDiff(
  draft: ModuleDraft,
  prior: ModuleDraft | undefined,
): OrgModuleAccessPatch | null {
  const hadAccess = prior?.hasAccess === true;

  if (!draft.hasAccess) {
    return hadAccess ? { has_access: false } : null;
  }

  const patch: OrgModuleAccessPatch = {};
  // A NEW grant always names itself, which is also what makes grant-and-narrow one request: the
  // backend refuses a permissions patch on a module the admin neither holds nor is gaining here.
  if (!hadAccess) patch.has_access = true;
  if (prior === undefined || draft.tier !== prior.tier) patch.granted_role = draft.tier;

  const priorCustom = hadAccess ? (prior?.custom ?? null) : null;
  if (draft.custom === null) {
    // Explicit `null` = CLEAR, and it is only meaningful when a set is actually there to clear.
    if (priorCustom !== null) patch.permissions = null;
  } else if (priorCustom === null || !sameKeys(draft.custom, priorCustom)) {
    patch.permissions = [...draft.custom];
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

// ── The typed refusals ───────────────────────────────────────────────────────
//
// Every one is detected on the CODE, or for the one answer with none on WHO THE WRITE WAS FOR, never
// on the server's English. A sentence is not a contract, and a screen that matched on one breaks the
// day the wording improves.

/**
 * The offending permission keys on a 422 `UNKNOWN_PERMISSION_KEY`, or `null` when that is not what
 * happened.
 *
 * The whole PUT wrote nothing. It is unreachable through the dialog's own controls — every checkbox
 * comes from the catalog the same request is validated against — so seeing it means the catalog moved
 * under an open dialog, which is exactly when naming the keys beats a generic failure.
 *
 * ⚠ The context key on this surface is `module`, not the class-specific product word.
 */
export function unknownPermissionKeys(error: unknown): string[] | null {
  if (!isGivmoError(error) || firstErrorCode(error) !== UNKNOWN_PERMISSION_KEY) return null;
  const keys = firstErrorContext(error)?.keys;
  return Array.isArray(keys) ? keys.filter((key): key is string => typeof key === 'string') : [];
}

/**
 * A 422 refusing to take an admin's LAST module.
 *
 * ⚠ NEW ON THIS SURFACE — a class-specific console has no branch for it. An admin whose last stored
 * access fact is removed becomes indistinguishable from one who was never enrolled, and falls back to
 * a WIDER default set than the configuration that just narrowed them. So the refusal is the safe
 * answer, and the copy has to name the alternative rather than reading as a bug.
 */
export function isLastModuleRevokeRefusal(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === LAST_MODULE_REVOKE_REFUSED;
}

/**
 * Why the role change refused, in the editor's terms, or `null` when it did not answer one of its
 * own refusals (a generic failure, or the family's route miss on a backend without the route).
 *
 * Read on the CODE, like every refusal here, and on the status for the one answer that has none:
 * the last-Global-Admin guard is the class writer's own 400, inherited untranslated, with no code.
 *
 * ⚠ A BARE 400 IS THE LAST GLOBAL ADMIN ONLY ON A SELF-DEMOTION (`demotingSelf`). The route is
 * Global-Admin only, so a person demoting someone else is a Global Admin themselves, and "you're the
 * only one" cannot be true of them. The same route's other bare 400 is the family's "not an admin of
 * this organization" (the person left the team while the dialog was open). So for anyone else a bare
 * 400 is a generic failure.
 *
 * `malformed` covers the two codes these controls cannot produce — apps beside a Global Admin, and
 * the same app twice: seeing either is a defect in this build, not something a person can act on.
 */
export type RoleChangeRefusal =
  | { kind: 'choice-required' }
  | { kind: 'app-unavailable'; module: string | null }
  | { kind: 'not-global-admin' }
  | { kind: 'last-global-admin' }
  | { kind: 'malformed' };

export function roleChangeRefusal(
  error: unknown,
  { demotingSelf }: { demotingSelf: boolean },
): RoleChangeRefusal | null {
  if (!isGivmoError(error)) return null;
  const code = firstErrorCode(error);
  if (code === ROLE_CHANGE_CHOICE_REQUIRED) return { kind: 'choice-required' };
  if (code === ROLE_CHANGE_MODULE_UNAVAILABLE) {
    const module = firstErrorContext(error)?.module;
    return { kind: 'app-unavailable', module: typeof module === 'string' ? module : null };
  }
  if (code === ROLE_CHANGE_NOT_GLOBAL_ADMIN) return { kind: 'not-global-admin' };
  if (code === ROLE_CHANGE_MODULES_WITH_GLOBAL_ADMIN || code === ROLE_CHANGE_DUPLICATE_MODULE) {
    return { kind: 'malformed' };
  }
  if (demotingSelf && error.status === 400 && code === null) return { kind: 'last-global-admin' };
  return null;
}

/**
 * The two role-change refusals that say the RECORD moved under the open dialog: the person is no
 * longer a Global Admin (the 409), or an app left the organization's list (the 422 unavailable). Their
 * words send the person to Cancel and reopen, so what the page shows behind the dialog is re-read at
 * once. Neither depends on who is being changed.
 */
export function isStaleRoleChangeRefusal(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  const code = firstErrorCode(error);
  return code === ROLE_CHANGE_NOT_GLOBAL_ADMIN || code === ROLE_CHANGE_MODULE_UNAVAILABLE;
}

/**
 * The last-Global-Admin guard on the REMOVAL path.
 *
 * ⚠ READ ON THE CODE, unlike the role change's twin ({@link roleChangeRefusal}), whose guard is the
 * CLASS writer's own bare 400 and has to be inferred from who the write was for. The removal route
 * translates that same class guard into the console's own typed **422 `LAST_GLOBAL_ADMIN_REFUSED`**,
 * so there is a code to read. Detecting a removal refusal by status alone would swallow the 400 the
 * same route answers for a target who is not an admin — "already removed", which is not a failure
 * and must not paint like one.
 */
export function isLastGlobalAdminRemovalRefusal(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === LAST_GLOBAL_ADMIN_REFUSED;
}

/**
 * The removal answered 400: that person is not an admin of this organization.
 *
 * ⚠ **NOT AN ERROR STATE, and this is the one refusal on this surface a screen should treat as
 * success.** The backend gives one constant answer for "no such user", "an admin of a DIFFERENT
 * organization" and "you already removed them" — deliberately, so ids are not enumerable across
 * organizations. From a console that is looking at its own roster, only the third is reachable in
 * practice: someone else removed them, or this session raced itself. The person is gone either way,
 * which is what the caller asked for.
 *
 * ⚠ STATUS-ONLY BY DESIGN, on one assumption: the ONLY reachable 400 on the org-level DELETE today is
 * that constant refusal (the constant carries no `error_code` to match — anti-enumeration). The class
 * writers' own 400s are pre-empted by the service's read-before-write, and their last-Owner refusals
 * are translated to a 422. A future DISTINCT 400 on this route must revisit this predicate, or it
 * will be read as success.
 */
export function isAlreadyNotAMember(error: unknown): boolean {
  return isGivmoError(error) && error.status === 400;
}
